import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image } from 'antd';
import { klona } from 'klona';
import { Icon } from '@fuxi/eevee-icon';
import { ImagePreviewCompare } from '@/components/ImagePreviewCompare';
import DeleteIcon from '@/assets/icon/Delete.svg';
import { useCustomCamera } from '@/hooks';
import { Toast } from '@/utils';
import { FieldName, MeasurementValue } from '../enumData';
import InputValue from './input-value';
import cx from './index.module.scss';

type UploadFileUrls = {
  value: string;
  name: string;
};

interface Props {
  afterMassage?: boolean;
  beforeHandData: MeasurementResult;
  onResultChange?: (value: MeasurementResult) => void;
  bodyMeasuresItems: MeasurementValue[];
  lastGirthMeasurementList?: FieldName[];
  handleScrollElement?: VoidFunc;
}

const updateOrAddValue = (array: UploadFileUrls[], name: string, newValue) => {
  let found = false;
  array?.forEach(item => {
    if (item?.name === name) {
      item.value = newValue;
      found = true;
    }
  });
  if (!found) {
    array.push({ name: name, value: newValue });
  }
  return array;
};

const BodyMeasurementComponent = ({
  afterMassage = false,
  beforeHandData,
  bodyMeasuresItems,
  onResultChange,
  lastGirthMeasurementList,
  handleScrollElement,
}: Props) => {
  const imagePreviewContainer = useRef(null);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  // 只有测量数据
  const [allMeasurementValues, setAllMeasurementValues] = useState<MeasurementResult>(null);
  // 只有图片
  const [uploadFileUrls, setUploadFileUrls] = useState<UploadFileUrls[]>([]);
  const { handleScan } = useCustomCamera();

  const allImageMeasuresItems = useMemo(() => {
    //将bodyMeasuresItems数组中measuredItemValue属性平铺 获取图片的index
    return bodyMeasuresItems
      ?.map(item =>
        item.measuredItemValue.map(innerItem => ({
          imageFiledName: innerItem?.imageFiledName,
          imageSrc: '',
        }))
      )
      .reduce((acc, val) => acc.concat(val), []);
  }, [bodyMeasuresItems]);

  const beforeImgList = useMemo(() => {
    return allImageMeasuresItems?.map(item => {
      return {
        ...item,
        imageSrc: beforeHandData?.[item.imageFiledName],
      };
    });
  }, [allImageMeasuresItems, beforeHandData]);

  const afterImgList = useMemo(() => {
    return allImageMeasuresItems?.map(item => {
      return {
        ...item,
        imageSrc: uploadFileUrls?.find(upload => upload?.name === item?.imageFiledName)?.value,
      };
    });
  }, [allImageMeasuresItems, uploadFileUrls]);

  const inputValueFiledNames = useCallback(
    (measuredItemValue: FieldName[]) => {
      if (afterMassage) {
        const FiledNames = measuredItemValue?.map(item => {
          const beforeVal = beforeHandData?.[item.name];
          const lastVal = lastGirthMeasurementList?.find(itm => itm?.name === item?.name)?.value;
          return {
            ...item,
            defaultValue: beforeVal || lastVal || item?.defaultValue,
          };
        });
        return FiledNames;
      } else {
        const FiledNames = measuredItemValue?.map(item => {
          const lastVal = lastGirthMeasurementList?.find(itm => itm?.name === item?.name)?.value;
          return {
            ...item,
            defaultValue: lastVal || item?.defaultValue,
          };
        });
        return FiledNames;
      }
    },
    [afterMassage, beforeHandData, lastGirthMeasurementList]
  );

  const onResult = useCallback(
    (uploadFileUrls: UploadFileUrls[], allMeasurementValues: MeasurementResult) => {
      setUploadFileUrls(uploadFileUrls);

      const arrayToObject = (arr: UploadFileUrls[]) => {
        const obj = {};
        arr?.map(item => {
          obj[item?.name] = item?.value;
        });

        return obj;
      };
      //厘米变成毫米
      const newMeasurementValues = klona(allMeasurementValues);

      for (let key in newMeasurementValues) {
        if (newMeasurementValues?.hasOwnProperty(key)) {
          newMeasurementValues[key] = Math.round(newMeasurementValues?.[key] * 10); // 或者使用 Math 方法执行其他的数学运算，例如 Math.floor() 或 Math.ceil()
        }
      }

      const imgs = arrayToObject(uploadFileUrls);
      const results = { ...imgs, ...newMeasurementValues };

      onResultChange(results);
    },
    [onResultChange]
  );

  const handleDeleteImg = useCallback(
    (name: string) => {
      const newUrls = klona(uploadFileUrls);
      const index = newUrls?.findIndex(item => item?.name === name);
      newUrls[index] = { value: '', name };

      onResult(newUrls, allMeasurementValues);

      Toast.show('已删除该图片');
    },
    [allMeasurementValues, onResult, uploadFileUrls]
  );

  const handleInputValue = useCallback(
    value => {
      const mergeObjects = (a, b) => {
        if (a === null) {
          return b;
        }
        for (const key in b) {
          a[key] = b[key];
        }
        return a;
      };

      const newAllMeasurementValues = klona(allMeasurementValues);
      const merged = mergeObjects(newAllMeasurementValues, value);
      setAllMeasurementValues(merged);
      onResult(uploadFileUrls, merged);
    },
    [allMeasurementValues, onResult, uploadFileUrls]
  );

  useEffect(() => {
    if (!afterMassage) {
      const newVal = klona(beforeHandData);

      // 去除额外的属性 只保留测量数据
      for (let key in newVal) {
        if (key?.includes('Image') || newVal?.[key] === null) {
          delete newVal[key];
        }
      }
      setAllMeasurementValues(newVal);
      const newImg = beforeImgList
        ?.filter(item => !!item.imageSrc)
        ?.map(item => {
          if (!!item?.imageSrc) {
            return {
              value: item?.imageSrc,
              name: item?.imageFiledName,
            };
          }
          return;
        });
      onResult(newImg, newVal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beforeHandData]);

  return (
    <div className={cx('body-measurement')}>
      <div className={cx('motions')}>
        {bodyMeasuresItems?.map((item, index) => (
          <div
            className={cx('motion-item', { 'extra-style': bodyMeasuresItems?.length === 1 })}
            key={item.bodyPartName}
          >
            {/* 服务前的不支持填写 */}
            {afterMassage && (
              <div className={cx('value')}>
                <InputValue
                  value={beforeHandData}
                  beforeMassage
                  lastMeasurement={lastGirthMeasurementList}
                  bodyPartLabel={item.bodyPartName}
                  filedNames={item.measuredItemValue}
                />
              </div>
            )}

            <div className={cx('value')}>
              <InputValue
                value={allMeasurementValues}
                afterMassage={afterMassage}
                bodyPartLabel={item.bodyPartName}
                filedNames={inputValueFiledNames(item.measuredItemValue)}
                onChange={value => handleInputValue(value)}
              />
            </div>

            <div className={cx('item', 'motion-example')}>
              <Image
                src={item.exampleImageSrc}
                alt="motion-item"
                className={cx({ 'motion-example-img': bodyMeasuresItems?.length > 1 })}
                preview={{
                  maxScale: 5,
                  imageRender: (originalNode, info) => {
                    return (
                      <div className={cx('preview-image')}>
                        <div className={cx('img-wrapper')}>{originalNode}</div>
                      </div>
                    );
                  },
                }}
              />
              <div className={cx('tip', 'example-tip')}>{item.bodyPartName}测量示范</div>
            </div>
            <ImagePreviewCompare
              beforeImgList={beforeImgList?.map(item => item.imageSrc) || []}
              afterImgList={afterImgList?.map(item => item?.imageSrc) || []}
              previewIndex={previewIndex}
              updatePreviewIndex={setPreviewIndex}
              isNeedImgGroup={!!afterMassage}
              noBeforeUploadText="服务前围度测量未上传"
              noAfterUploadText="补充围度复量"
              totalCompareLength={allImageMeasuresItems?.length}
            >
              <>
                {afterMassage && (
                  <div
                    className={cx('upload-wrapper', { 'extra-upload-wrapper': item?.measuredItemValue?.length > 1 })}
                  >
                    {item?.measuredItemValue?.map((itm, idx) => (
                      <div className={cx('item', 'pre-upload')} key={itm.name}>
                        {beforeHandData?.[itm.imageFiledName] ? (
                          <>
                            <Image
                              onClick={() =>
                                setPreviewIndex(
                                  allImageMeasuresItems?.findIndex(item => item.imageFiledName === itm.imageFiledName)
                                )
                              }
                              preview={{ maxScale: 5 }}
                              className={cx('upload-img', { 'extra-upload-img': item?.measuredItemValue?.length > 1 })}
                              src={beforeHandData?.[itm.imageFiledName]}
                              alt="motion-item"
                            />
                            <div className={cx('img-tip')}>服务前</div>
                          </>
                        ) : (
                          <div className={cx('skip-tip')}>
                            <div>服务前</div>
                            <div>围度测量图片未上传</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className={cx('upload-wrapper', { 'extra-upload-wrapper': item?.measuredItemValue?.length > 1 })}>
                  {item?.measuredItemValue?.map((itm, idx) => (
                    <div className={cx('item', 'upload-box')} ref={imagePreviewContainer} key={itm.name}>
                      <div className={cx('icon-wrapper')}>
                        {uploadFileUrls?.find(upload => upload?.name === itm?.imageFiledName)?.value ? (
                          <>
                            <Image
                              src={uploadFileUrls?.find(upload => upload?.name === itm?.imageFiledName)?.value}
                              alt="upload-img"
                              preview={{
                                maxScale: 5,
                                imageRender: (originalNode, info) => {
                                  return (
                                    <div className={cx('preview-image')}>
                                      <div className={cx('img-wrapper')}>{originalNode}</div>
                                      <div className={cx('tag')}>
                                        <span>服务前</span>
                                      </div>
                                    </div>
                                  );
                                },
                              }}
                              className={cx('upload-img', { 'extra-width': item?.measuredItemValue?.length > 1 })}
                              onClick={() =>
                                setPreviewIndex(
                                  allImageMeasuresItems?.findIndex(item => item?.imageFiledName === itm?.imageFiledName)
                                )
                              }
                            />
                            <div className={cx('img-tip', { after: afterMassage })}>
                              {afterMassage ? '服务后' : '服务前'}
                            </div>

                            {/* 删除图标 */}
                            <img
                              src={DeleteIcon}
                              alt="delete"
                              onClick={() => handleDeleteImg(itm.imageFiledName)}
                              className={cx('delete')}
                            />
                          </>
                        ) : (
                          <div
                            className={cx('add-wrapper', { 'extra-width': item?.measuredItemValue?.length > 1 })}
                            onClick={async () => {
                              if (afterMassage) {
                                handleScrollElement();
                              }
                              const { url } = await handleScan({ width: 0, url: '' });
                              const newUrls = klona(uploadFileUrls);
                              onResult(updateOrAddValue(newUrls, itm?.imageFiledName, url), allMeasurementValues);
                            }}
                          >
                            <Icon name="camera_2" size={32} />
                            {item?.measuredItemValue?.length > 1 ? (
                              <div>{itm?.label}拍照上传</div>
                            ) : (
                              <div>拍照上传</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            </ImagePreviewCompare>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BodyMeasurementComponent;
